import { useState } from 'react';
import {
  Alert,
  Button,
  Icon,
  Modal,
  ModalActions,
  SearchSelect,
  TextArea,
  type IconName,
} from '@evogard/ui';

/**
 * Os registros da aba Atividade que a casa repete em mais de uma mesa — o
 * padrão do sinistro, desde 28/10/2026 também o do Financeiro: a pendência é
 * um REGISTRO na Atividade, com o botão no alto da aba e um modal pequeno com
 * a mensagem. Quem grava, e o que a pendência faz com o card, é de cada mesa.
 */

/** Um registro da Atividade: o botão e o modal dele. */
export interface ActivityRecord<C extends string = string> {
  code: C;
  icon: IconName;
  title: string;
  /** Para o card: some quando ele já está parado — não se para duas vezes. */
  stops: boolean;
}

/**
 * Os botões "Registrar na Atividade". A mesma peça na mesa do sinistro, na de
 * Periféricos (19/10/2026) e na da despesa no Financeiro (28/10/2026): quem
 * chama diz quais registros tem, o que cada um abre e se o card já está parado.
 */
export function ActivityRecordButtons<C extends string>({
  records,
  held,
  heldTitle,
  onOpen,
}: {
  records: readonly ActivityRecord<C>[];
  /** O card já está parado: o registro que para some — não se para duas vezes. */
  held: boolean;
  /** Por que o registro que para está desligado. */
  heldTitle: string;
  onOpen: (record: C) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Registrar na Atividade"
      className="flex flex-col gap-1.5"
    >
      <h3 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
        Registrar na Atividade
      </h3>
      <div className="grid gap-2 sm:grid-cols-2">
        {records.map((r) => (
          <button
            key={r.code}
            type="button"
            disabled={r.stops && held}
            title={r.stops && held ? heldTitle : undefined}
            onClick={() => onOpen(r.code)}
            className="flex min-h-11 items-center gap-2.5 rounded-borda border border-borda bg-superficie px-3 py-2 text-left text-sm font-semibold text-texto enabled:hover:border-borda-forte disabled:cursor-not-allowed disabled:opacity-55"
          >
            <Icon nome={r.icon} size={16} className="shrink-0 text-texto-suave" />
            <span className="min-w-0 flex-1">{r.title}</span>
            <Icon nome="mais" size={16} className="shrink-0 text-texto-tenue" />
          </button>
        ))}
      </div>
    </div>
  );
}

/** O texto de cada parada que se registra com justificativa. */
const HOLD_COPY = {
  pendencia: {
    title: 'Pendência',
    icon: 'pausa',
    field: 'Mensagem de pendência',
    action: 'Parar por pendência',
  },
  pre_desistencia: {
    title: 'Pedido de desistência',
    icon: 'sair',
    field: 'Justificativa da desistência',
    action: 'Registrar o pedido',
  },
  /* O jurídico (30/10/2026): pede a justificativa como a pendência. */
  juridico: {
    title: 'Jurídico',
    icon: 'livro',
    field: 'Justificativa do jurídico',
    action: 'Parar no jurídico',
  },
} as const satisfies Record<
  string,
  { title: string; icon: IconName; field: string; action: string }
>;

/**
 * O modal da pendência, do jurídico e do pedido de desistência — o mesmo na
 * mesa do sinistro e na de Periféricos (19/10/2026). Quem grava é `submit`.
 */
export function HoldDialog({
  type,
  description,
  kinds,
  submit,
  onClose,
}: {
  type: keyof typeof HOLD_COPY;
  description: string;
  /** Os tipos de pendência, onde o departamento os tem. */
  kinds?: Record<string, string>;
  submit: (input: { text: string; pendencyKind?: string }) => Promise<unknown>;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [kind, setKind] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const copy = HOLD_COPY[type];
  const kindMissing = kinds !== undefined && kind === null;

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={copy.title}
      descricao={description}
      icon={copy.icon}
      tone="destaque"
      size="sm"
    >
      <div className="flex flex-col gap-4">
        {kinds && (
          <SearchSelect
            label="Tipo de pendência"
            required
            options={Object.entries(kinds).map(([value, label]) => ({ value, label }))}
            value={kind ?? ''}
            onChange={(v) => setKind(v || null)}
            clearable
          />
        )}
        <TextArea
          label={copy.field}
          required
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={4000}
          rows={4}
        />
        {error && <Alert tone="erro">{error}</Alert>}
        <ModalActions>
          <Button variant="secundario" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={text.trim() === '' || kindMissing}
            loading={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              submit({
                text: text.trim(),
                ...(kinds && kind ? { pendencyKind: kind } : {}),
              })
                .then(onClose, (e: Error) => setError(e.message))
                .finally(() => setBusy(false));
            }}
          >
            {copy.action}
          </Button>
        </ModalActions>
      </div>
    </Modal>
  );
}
